import React, { useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { InputWrapper, Link, OverlaySpinner, PageWrapper, Select, TableWithPagination } from '../../components'
import { getUserUuidFromLocalStorage } from '../../utils'
import { WemoloAccordion } from '../../components/Wemolo'
import { useSearchDeviceConfig } from './utils'
import {
    CONFIGURATIONS,
    ConfigurationItem,
    getConfigurationById,
    transformConfigurationToSelectValue,
    transformSelectValueToConfiguration
} from './utils/Configuration'
import * as Sentry from '@sentry/react'
import styles from './SearchConfigView.module.css'

export const SearchConfigView = () => {
    const location = useLocation()
    const history = useHistory()
    const queryParams = new URLSearchParams(location.search)
    const urlConfiguration = getConfigurationById(queryParams.get('configuration'))
    const [configuration, setConfiguration] = React.useState<ConfigurationItem | null>(urlConfiguration)
    const { data, loading } = useSearchDeviceConfig(configuration?.path, configuration?.value)
    const matching = data?.matching_devices
    const wrongPath = data?.non_matching_devices

    const mapDevicesToTable = (devices: any[] | undefined) => {
        return devices?.map((device) => ({
            ...device,
            stations: device?.general_config?.stations.map((station: any) => (
                <React.Fragment key={station.station_name}>
                    <Link
                        link={`/station?stationName=${station.station_name}`}
                        target="_self"
                        text={station.station_name}
                    />
                    &nbsp; &nbsp;
                </React.Fragment>
            )),
            device_uuid: (
                <Link link={`/device?deviceId=${device?.device_uuid}`} target="_self" text={device?.device_uuid} />
            )
        }))
    }
    const mappedMatchingToTable = useMemo(() => mapDevicesToTable(matching), [matching])
    const mappedNonMatchingToTable = useMemo(() => mapDevicesToTable(wrongPath), [wrongPath])

    useEffect(() => {
        Sentry.logger.info('search_config.viewed', {
            event: 'search_config_viewed',
            pathname: location.pathname,
            userId: getUserUuidFromLocalStorage()
        })
    }, [])

    return (
        <PageWrapper>
            <WemoloAccordion title="Select configuration" defaultExpanded={true}>
                <InputWrapper label="">
                    <div className={styles.selectRow}>
                        <div className={styles.selectField}>
                            <Select
                                isLoading={loading}
                                onChange={(option) => {
                                    if (option && option.id) {
                                        history.push(`?configuration=${option.id}`)
                                    } else {
                                        history.push(`/configuration`)
                                    }
                                    setConfiguration(transformSelectValueToConfiguration(option))
                                }}
                                value={configuration}
                                options={CONFIGURATIONS.map(transformConfigurationToSelectValue)}
                            />
                        </div>
                    </div>
                </InputWrapper>
            </WemoloAccordion>
            <WemoloAccordion
                key={`matching-${Boolean(matching)}`}
                title={`Matching Devices ${matching ? '(' + matching.length + ')' : ''}${loading ? '(Loading)' : ''}`}
                defaultExpanded={Boolean(matching) || false}
            >
                <OverlaySpinner loading={loading}>
                    <TableWithPagination
                        tableProps={{
                            columns: [
                                { label: 'Device Name', key: 'device_name' },
                                { label: 'Device UUID', key: 'device_uuid' },
                                { label: 'Stations', key: 'stations' }
                            ],
                            data: mappedMatchingToTable || []
                        }}
                    />
                </OverlaySpinner>
            </WemoloAccordion>
            <WemoloAccordion
                key={`non-matching-${Boolean(wrongPath)}`}
                title={`Devices with no such config ${
                    mappedNonMatchingToTable ? '(' + mappedNonMatchingToTable?.length + ')' : ''
                }`}
                defaultExpanded={Boolean(wrongPath) || false}
            >
                <OverlaySpinner loading={loading}>
                    <TableWithPagination
                        tableProps={{
                            columns: [
                                { label: 'Device Name', key: 'device_name' },
                                { label: 'Device UUID', key: 'device_uuid' },
                                { label: 'Stations', key: 'stations' }
                            ],
                            data: mappedNonMatchingToTable || []
                        }}
                    />
                </OverlaySpinner>
            </WemoloAccordion>
        </PageWrapper>
    )
}
